import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileSignature, Loader2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { formatINR } from "@/lib/utils";
import { ProposalStatusBadge } from "@/components/proposals/ProposalStatusBadge";
import { UploadedTag } from "@/components/proposals/UploadedTag";
import { NewProposalDialog } from "@/components/proposals/NewProposalDialog";
import type { ProposalListRow } from "@/types/proposal";

/** Stages at which a new proposal can be started (stages without a key are not gated) */
const PROPOSAL_STAGES = ["QUALIFICATION", "PROPOSAL"];

interface DealProposalsCardProps {
  dealId: string;
  dealName: string;
  stageKey?: string | null;
  isClosed: boolean;
}

export function DealProposalsCard({ dealId, dealName, stageKey, isClosed }: DealProposalsCardProps) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState<ProposalListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);

  const canRead = hasPermission("proposal.read");
  const stageAllows = !stageKey || PROPOSAL_STAGES.includes(stageKey);
  const canCreate = hasPermission("proposal.create") && !isClosed && stageAllows;

  useEffect(() => {
    if (!canRead) return;
    setLoading(true);
    api
      .get(`/api/Proposal/List?deal_id=${dealId}&page_size=100`)
      .then((res) => setRows(unwrapPage<ProposalListRow>(res).rows))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [dealId, canRead]);

  if (!canRead) return null;

  // Superseded versions are reachable from the newer version
  const visible = rows.filter((r) => r.proposal_status !== "SUPERSEDED");

  return (
    <div className="bg-card border rounded-2xl shadow-xs overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <FileSignature className="size-4 text-primary" />
          <h3 className="text-sm font-bold text-foreground">Proposals</h3>
          {visible.length > 0 && <span className="text-[10px] px-1.5 rounded-full bg-muted text-muted-foreground">{visible.length}</span>}
        </div>
        {canCreate && (
          <Button size="sm" variant="outline" onClick={() => setNewOpen(true)} className="h-8 gap-1.5 text-xs">
            <Plus className="size-3.5" />
            New Proposal
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : visible.length === 0 ? (
        <div className="py-5 px-4 text-center text-xs text-muted-foreground">
          No proposals yet.
          {canCreate
            ? " Write one to show the customer what you'll deliver and an indicative price."
            : !isClosed && !stageAllows
              ? " Proposals are written at the Qualification or Proposal stage."
              : ""}
        </div>
      ) : (
        <ul className="divide-y">
          {visible.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => navigate(`/proposals/${r.id}`)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-muted/50"
              >
                <div className="min-w-0">
                  <div className="font-mono text-xs font-semibold">{r.display_number}<UploadedTag source={(r as any).source} /></div>
                  <div className="text-[11px] text-muted-foreground truncate">{r.title}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-semibold">{formatINR(r.estimate_total)}</span>
                  <ProposalStatusBadge status={r.proposal_status} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <NewProposalDialog open={newOpen} onOpenChange={setNewOpen} dealId={dealId} dealName={dealName} />
    </div>
  );
}
